import { useState } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { useQuery } from '@apollo/client'
import NativeQRCode from 'react-native-qrcode-svg'
import { useTranslation } from 'react-i18next'

import { GET_USER } from '@/constants/graphql/queries'
import { marketPresentation } from '@/config/marketPresentation'
import { copyToClipboard } from '@/utils/copy'
import { AuthContext } from '@/stores/auth'
import { useToast } from '@/components/base'
import { Copy, Spinner } from '@/components/icon'
import BrandLogo from '@/components/BrandLogo'
import { Stack } from '@/components/layout'
import NavigationBar from '@/components/NavigationBar'

export default function SelectAssetScreen() {
  const { t } = useTranslation()
  const toast = useToast()
  const user = AuthContext.useSelector((state) => state.context.values?.user)
  const [ybbAddress, setYbbAddress] = useState<string>('−')

  function handleCopy(text: string) {
    copyToClipboard(text)
    toast.show({ variant: 'success', description: t('ui:copySuccess') })
  }

  useQuery(GET_USER, {
    variables: {
      id: user?.id
    },
    onCompleted: (data) => {
      setYbbAddress(data.user.ybbAddress)
    },
    onError: (error) => {
      console.error('error:', error)
      toast.show({ variant: 'warning', description: t('ui:depositAddressLoadFailed') })
    }
  })

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar>{t('ui:deposit')}</NavigationBar>
        }}
      />
      <ScrollView className={'bg-background-secondary pb-4'}>
        <View className={'mx-4 mt-4 items-center rounded-lg border border-border-10 bg-background-primary px-4 py-6'}>
          <View className={'flex-row items-center gap-2'}>
            <BrandLogo className={'size-6'} />
            <Text className={'text-base font-medium text-text-primary'}>{marketPresentation.platformToken.displayName}</Text>
          </View>
          <View className={'h-4'} />
          <View className={'w-full max-w-[256px]'}>
            <View className={'border border-border-10 p-4'}>
              {ybbAddress !== '−' ? (
                <View className={'items-center'}>
                  <NativeQRCode value={ybbAddress} size={224} />
                </View>
              ) : (
                <View className={'aspect-square items-center justify-center'}>
                  <View className={'animate-spin'}>
                    <Spinner className={'size-6 text-primary'} />
                  </View>
                </View>
              )}
            </View>
          </View>
          <View className={'h-3'} />
          {ybbAddress && (
            <View className={'w-full'}>
              <Pressable
                className={'mx-auto max-w-fit flex-row gap-1 rounded-lg bg-primary-subtle px-2 py-1.5'}
                onPress={() => handleCopy(ybbAddress)}
              >
                <Text className={'overflow-hidden text-ellipsis text-xs font-medium text-primary'}>{ybbAddress}</Text>
                {ybbAddress !== '−' && <Copy className={'size-4 min-w-4 text-primary'} />}
              </Pressable>
            </View>
          )}
        </View>
        <HintSection />
      </ScrollView>
    </>
  )
}

function HintSection() {
  const { t } = useTranslation()

  return (
    <>
      <View className={'bg-background-secondary px-4 pb-2 pt-5'}>
        <Text className={'text-sm text-text-primary'}>{t('ui:depositNotice')}</Text>
      </View>
      <Text className={'mx-4 text-sm'}>
        <Text className={'text-text-secondary'}>{t('ui:depositNoticePrefix')}</Text>
        <Text className={'text-text-danger'}>{` ${marketPresentation.platformToken.displayName} `}</Text>
        <Text className={'text-text-secondary'}>{t('ui:depositNoticeSuffix')}</Text>
      </Text>
    </>
  )
}
